import React, { useState } from 'react';

import ArticleIcon from '@mui/icons-material/Article';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FavoriteIcon from '@mui/icons-material/Favorite';
import GroupIcon from '@mui/icons-material/Group';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SearchIcon from '@mui/icons-material/Search';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { PageLayout } from '@klokhuis/core/PageLayout';

import styles from './AgentsKnowledgeHubContent.module.css';

interface Article {
  id: string;
  category: string;
  readTime: string;
  title: string;
  summary: string;
  icon: React.ReactNode;
  tags: string[];
  content: React.ReactNode;
}

const articles: Article[] = [
  {
    id: '1',
    category: 'Platform Features',
    readTime: '4 min read',
    title: 'How to Use Direct Chat on FindMyProps to Close Deals Faster',
    summary:
      'Learn how FindMyProps\'s built-in chat connects you instantly with verified buyers — no middlemen, no missed leads.',
    icon: <ChatBubbleIcon />,
    tags: ['Direct Chat', 'Lead Conversion', 'FindMyProps Features'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          In the competitive Indian real estate market, speed is everything. A buyer who inquires about a property at 7 PM expects a response before they move on to the next listing. <strong>FindMyProps Direct Chat</strong> ensures you are always within reach — directly from the listing page.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          What is Direct Chat?
        </Typography>
        <Typography variant="body1" paragraph>
          Direct Chat is FindMyProps's in-platform messaging system that connects agents and sellers with buyers in real time. Unlike portals that hide buyer contact details behind paywalls or delay email forwarding, Direct Chat puts the conversation in your hands immediately.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Why It Matters for Indian Agents
        </Typography>
        <Typography variant="body1" paragraph>
          Indian buyers — whether searching for plots in Chennai outskirts or residential villas in Coimbatore — often inquire on multiple portals simultaneously. The first agent to respond almost always wins the appointment. With Direct Chat, you can:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Reply instantly to buyer queries from your FindMyProps dashboard</Typography></li>
          <li><Typography variant="body1">Share additional photos, documents, and price details in-thread</Typography></li>
          <li><Typography variant="body1">Keep a full conversation history for every prospect</Typography></li>
          <li><Typography variant="body1">Never lose a lead to an unanswered SMS or missed call</Typography></li>
        </Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Pro Tips for Maximum Conversion
        </Typography>
        <Typography variant="body1" paragraph>
          Enable chat notifications on your mobile so you respond within minutes. Personalize your opening message — reference the specific property the buyer enquired about. Buyers feel more confident when the agent demonstrates they have read the inquiry properly. Aim to move from chat to a physical site visit within 48 hours for the best conversion rates.
        </Typography>
        <Typography variant="body1" paragraph>
          Direct Chat also builds a documented trail, which is useful if you need to follow up weeks later. Many deals in Indian real estate take 30–90 days from first contact to registration — a searchable chat history keeps the relationship warm.
        </Typography>
      </>
    ),
  },
  {
    id: '2',
    category: 'Verified Buyers & Trust',
    readTime: '5 min read',
    title: 'Why Verified Buyers on FindMyProps Mean Higher-Quality Leads',
    summary:
      'Discover how FindMyProps\'s verified buyer and agent system filters out time-wasters so you focus on serious prospects.',
    icon: <VerifiedIcon />,
    tags: ['Verified Buyers', 'Lead Quality', 'Trust & Safety'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          Every agent in India knows the pain of spending an afternoon on a site visit only to discover the "buyer" had no real budget or intent. <strong>FindMyProps solves this at the source</strong> by verifying both buyers and agents before they can engage on the platform.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          How the Verification Process Works
        </Typography>
        <Typography variant="body1" paragraph>
          Buyers on FindMyProps complete a phone-number and OTP-based verification before they can contact an agent. Agents undergo an additional profile review process that validates their professional identity. This dual-layer verification means every chat notification you receive is from a real, accountable person.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          The Business Impact for Agents
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Fewer ghost inquiries and wasted site visits</Typography></li>
          <li><Typography variant="body1">Higher close rates per lead because intent is pre-screened</Typography></li>
          <li><Typography variant="body1">Better reputation management — verified agents receive priority visibility</Typography></li>
          <li><Typography variant="body1">Legal protection — verified digital trail for every buyer interaction</Typography></li>
        </Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Trust as a Competitive Advantage
        </Typography>
        <Typography variant="body1" paragraph>
          In a market still rebuilding trust after high-profile builder defaults, being on a verified platform sends a strong signal. Buyers looking at your listings on FindMyProps already know they are dealing with a screened professional. Use this in your own marketing: <em>"Find me on FindMyProps — verified agent, transparent deals."</em>
        </Typography>
      </>
    ),
  },
  {
    id: '3',
    category: 'Buyer Engagement',
    readTime: '4 min read',
    title: 'React to Property: The Feature That Tells You Exactly What Buyers Want',
    summary:
      'Understand how the "React to Property" feature gives agents real-time buyer sentiment data to price and present listings better.',
    icon: <FavoriteIcon />,
    tags: ['React to Property', 'Buyer Insights', 'Listing Optimization'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          Pricing a property correctly in tier-2 Indian cities is notoriously difficult. Comparable sales data is sparse, and asking prices are often speculative. <strong>FindMyProps's React to Property feature</strong> gives you live market feedback that no offline broker network can provide.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          What "React to Property" Does
        </Typography>
        <Typography variant="body1" paragraph>
          Buyers can react to any listing — expressing interest, saving it, or flagging it as over-priced. As the listing agent, you see an aggregated sentiment score for each of your properties in your dashboard. High reaction counts with strong positive sentiment confirm your pricing is competitive. A surge of saves with zero inquiries often signals the price needs adjustment.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Using Reaction Data to Win Mandates
        </Typography>
        <Typography variant="body1" paragraph>
          When pitching a new mandate, show the seller the reaction data from comparable listings. Saying "properties like yours at this price point received 45 saves but only 2 inquiries last month" is far more persuasive than a vague market assessment. It demonstrates data-driven expertise — the hallmark of a modern Indian real estate professional.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Optimising Your Listings for More Reactions
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Upload minimum 8–10 high-resolution photos including plot dimensions and surrounding infrastructure</Typography></li>
          <li><Typography variant="body1">Write descriptions in both English and Tamil/Telugu/Kannada as relevant</Typography></li>
          <li><Typography variant="body1">Include nearby landmarks, school distances, and road access details</Typography></li>
          <li><Typography variant="body1">Update listings whenever there is a price revision — re-activated listings appear fresh in feeds</Typography></li>
        </Box>
      </>
    ),
  },
  {
    id: '4',
    category: 'Digital Marketing',
    readTime: '6 min read',
    title: 'SEO for Real Estate Agents: How to Rank Your Property Listings in India',
    summary:
      'A practical SEO playbook for Indian agents — from keyword research to local search optimization — that drives organic buyer traffic.',
    icon: <SearchIcon />,
    tags: ['SEO', 'Digital Marketing', 'Organic Traffic'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          Most Indian real estate agents rely entirely on portals and word of mouth. Those who understand search engine optimisation (SEO) build a sustainable pipeline of inbound leads at near-zero marginal cost. Here is how to start.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          1. Target Hyper-Local Keywords
        </Typography>
        <Typography variant="body1" paragraph>
          Broad terms like "plots for sale in Tamil Nadu" are dominated by large portals. Instead, target long-tail phrases: <em>"residential plots near Avinashi Road Coimbatore"</em>, <em>"farm land for sale in Hosur below 50 lakhs"</em>, <em>"2BHK villa Madurai bypass road"</em>. These convert at 3–5× higher rates because they reflect specific buyer intent.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          2. Optimise Your FindMyProps Listings
        </Typography>
        <Typography variant="body1" paragraph>
          FindMyProps listing pages are indexed by Google. Use the full property description field to naturally incorporate your target keywords. Include the locality name, landmark, distance to highway, and property type in the first two sentences. This is free SEO that directly benefits your listing's visibility.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          3. Build a Google Business Profile
        </Typography>
        <Typography variant="body1" paragraph>
          A verified Google Business Profile for your real estate agency appears in Google Maps searches. Link your FindMyProps profile in your website URL field. Request reviews from past clients — even five genuine reviews dramatically improve local pack ranking.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          4. Content Marketing Through Articles
        </Typography>
        <Typography variant="body1" paragraph>
          Write 500-word articles on topics like "Is Villupuram a good place to invest in 2025?" or "DTCP vs CMDA approved plots — what buyers in Tamil Nadu need to know." Share these on your social channels with links back to your FindMyProps profile. Over 6–12 months this builds topical authority and consistent organic traffic.
        </Typography>
      </>
    ),
  },
  {
    id: '5',
    category: 'Lead Generation',
    readTime: '5 min read',
    title: '10 Proven Strategies to Generate More Buyer Leads for Indian Real Estate Agents',
    summary:
      'Move beyond referrals with digital lead generation tactics that work in the Indian market — from WhatsApp campaigns to FindMyProps profile optimisation.',
    icon: <TrendingUpIcon />,
    tags: ['Lead Generation', 'Digital Marketing', 'WhatsApp Marketing'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          Referrals will always be the bedrock of Indian real estate, but in 2025 the agents with full pipelines combine referrals with systematic digital lead generation. Here are 10 strategies that work.
        </Typography>
        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Optimise your FindMyProps agent profile</strong> — Add a professional headshot, verified badge, and a bio that mentions your speciality areas and languages spoken. Profiles with photos receive 3× more clicks.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>WhatsApp broadcast lists</strong> — Segment buyers by property type and budget. Send property alerts only when a new listing matches their criteria. Respect opt-in consent.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>YouTube property tours</strong> — A 2-minute walkthrough video of a plot or house, optimised with the locality name in the title, consistently ranks in search results for hyper-local terms.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Instagram Reels</strong> — Short before/after development footage, neighbourhood tours, and "what you get for 30 lakhs in [city]" videos perform strongly in Tier 2 Indian markets.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Facebook Local Groups</strong> — Post listings in city-specific real estate investment groups. Engage genuinely — answer questions about DTCP approvals, EC status, and registration costs to build authority.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Google Ads for high-intent micro-markets</strong> — Run ads targeting "[locality] plots for sale" with a monthly budget as low as ₹3,000. Send traffic to your FindMyProps listings page.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Email newsletters to NRI communities</strong> — NRI buyers from UK, USA, UAE, and Singapore are a substantial segment for South Indian real estate. A monthly email with curated listings and market updates keeps you top of mind.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Partner with home loan DSAs</strong> — Buyers who are pre-approved for a loan are the most serious leads. Build referral relationships with home loan DSAs from major banks.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Online property expos</strong> — Participate in virtual property fairs. FindMyProps periodically hosts promotional campaigns — ensure your best listings are active and well-photographed during these windows.
            </Typography>
          </li>
          <li>
            <Typography variant="body1" paragraph>
              <strong>Google Reviews for your agency</strong> — Post-transaction, ask satisfied buyers to leave a Google Review. These are permanent social proof that rank in local searches and convert cold inquiries into warm leads.
            </Typography>
          </li>
        </Box>
      </>
    ),
  },
  {
    id: '6',
    category: 'Platform Features',
    readTime: '3 min read',
    title: 'How to Manage Your Property Listings Effectively on FindMyProps',
    summary:
      'A step-by-step guide to creating, updating, and tracking your listings on FindMyProps for maximum visibility and faster closings.',
    icon: <ArticleIcon />,
    tags: ['Listing Management', 'Platform Guide', 'Best Practices'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          A well-managed listing portfolio on FindMyProps is the foundation of your digital presence. This guide walks you through creating listings that attract buyers and keeping them optimised over time.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Creating a High-Converting Listing
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Select the exact property type — Residential Land, House/Villa, Farm Land, or Commercial Land. Correct categorisation ensures your listing appears in the right search filters.</Typography></li>
          <li><Typography variant="body1">Choose the most granular location available — street or locality level, not just city. Buyers filter by micro-markets.</Typography></li>
          <li><Typography variant="body1">Fill every available field — plot dimensions, facing direction, approved authority, distance to school, hospital, and main road. Completeness improves search ranking within the platform.</Typography></li>
          <li><Typography variant="body1">Upload at least 8 photos. Cover: main gate, plot corners, road frontage, internal layout, surrounding area, and any approved plan documents.</Typography></li>
        </Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Ongoing Listing Maintenance
        </Typography>
        <Typography variant="body1" paragraph>
          Review your active listings weekly. If a listing has been up for 30+ days without inquiries, consider refreshing the photos, revising the description, or adjusting the price. Updated listings get a recency boost in FindMyProps search results.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Tracking Performance
        </Typography>
        <Typography variant="body1" paragraph>
          Use the reaction and inquiry data from your listings dashboard to identify which properties are attracting attention. Double down on what works — if farm land listings consistently outperform residential land in your area, specialise accordingly and build keyword-optimised content around that niche.
        </Typography>
      </>
    ),
  },
  {
    id: '7',
    category: 'Market Insights',
    readTime: '5 min read',
    title: 'Top 5 Emerging Real Estate Markets in South India for 2025',
    summary:
      'Data-backed analysis of the micro-markets offering the best capital appreciation and rental yield for your buyer clients in 2025.',
    icon: <TrendingUpIcon />,
    tags: ['Market Insights', 'Investment', 'South India'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          As an agent, your value to buyers goes beyond showing properties — you are a market intelligence resource. Here are five South Indian micro-markets generating strong buyer interest in 2025.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          1. Hosur–Krishnagiri Corridor (Tamil Nadu)
        </Typography>
        <Typography variant="body1" paragraph>
          Manufacturing expansion driven by EV and electronics industries has created sustained demand for both residential plots and industrial land near Hosur. Infrastructure upgrades including the 8-lane NH-44 widening are compressing commute times to Bangalore's Electronic City, making Hosur one of the most active buy-and-hold markets for NRI investors.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          2. Coimbatore West — Kinathukadavu to Pollachi
        </Typography>
        <Typography variant="body1" paragraph>
          Prices remain accessible compared to Coimbatore East while benefiting from the same city's commercial growth. Strong NRI demand from Coimbatore diaspora in the UK and Gulf makes this a reliable mid-ticket investment corridor.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          3. Tiruppur Textile Township Expansion
        </Typography>
        <Typography variant="body1" paragraph>
          Export revenue growth is driving worker housing demand and commercial land appreciation around new Special Economic Zone boundaries. Residential plots within 5km of the proposed SIPCOT industrial zone are seeing 18–22% annual appreciation.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          4. Madurai–Dindigul IT Corridor
        </Typography>
        <Typography variant="body1" paragraph>
          The Tamil Nadu government's IT park development near Madurai is drawing young professionals seeking affordable housing compared to Chennai. Agricultural land conversion near NH-85 is creating early-mover opportunities in approved layouts.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          5. Krishnapatnam–Nellore Industrial Zone (Andhra Pradesh)
        </Typography>
        <Typography variant="body1" paragraph>
          Port-driven industrial growth and proposed PCPIR (Petroleum, Chemicals, and Petrochemicals Investment Region) development near Nellore makes this one of the highest-conviction commercial land opportunities in South India for 2025–2028.
        </Typography>
      </>
    ),
  },
  {
    id: '8',
    category: 'Agent Success',
    readTime: '4 min read',
    title: 'Building Your Personal Brand as a Real Estate Agent in India',
    summary:
      'Why personal branding is the highest-ROI investment a real estate agent can make and a practical roadmap to build yours.',
    icon: <EmojiEventsIcon />,
    tags: ['Personal Branding', 'Agent Success', 'Social Media'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          In a market where buyers often distrust agents, personal branding transforms you from an anonymous salesperson into a trusted advisor. Agents who invest in their personal brand close 40–60% more transactions per year, according to industry surveys.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Define Your Niche
        </Typography>
        <Typography variant="body1" paragraph>
          The most successful agent brands are specific. Instead of "real estate agent in Tamil Nadu," become "the farm land specialist in Erode district" or "the NRI investment advisor for Coimbatore residential plots." Niche positioning makes word-of-mouth referrals more precise and online content more searchable.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Your FindMyProps Profile as Brand Anchor
        </Typography>
        <Typography variant="body1" paragraph>
          Your FindMyProps agent profile is often the first touchpoint buyers have with you. Use a professional headshot — agents with quality photos are perceived as 72% more trustworthy in studies. Write a bio that tells a story: your years in the market, areas you specialise in, languages you speak, and what makes your service different.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Content Pillars for Real Estate Personal Branding
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1"><strong>Education:</strong> "How to read an EC (Encumbrance Certificate)" — practical, searchable, trust-building.</Typography></li>
          <li><Typography variant="body1"><strong>Market commentary:</strong> Monthly price trend posts for your speciality area.</Typography></li>
          <li><Typography variant="body1"><strong>Client stories:</strong> Anonymised success stories that showcase your problem-solving skills.</Typography></li>
          <li><Typography variant="body1"><strong>Behind the scenes:</strong> Site visit reels, registry office processes, due diligence walkthroughs.</Typography></li>
        </Box>
        <Typography variant="body1" paragraph>
          Consistency over 6–12 months compounds. Agents who post 3–4 times per week on LinkedIn and Instagram typically see organic inbound inquiries replace 30–50% of their offline prospecting effort.
        </Typography>
      </>
    ),
  },
  {
    id: '9',
    category: 'Legal & Compliance',
    readTime: '6 min read',
    title: 'RERA Compliance Checklist for Real Estate Agents in Tamil Nadu and Andhra Pradesh',
    summary:
      'Avoid penalties and build buyer trust with this practical RERA compliance checklist for registered agents operating in South India.',
    icon: <CheckCircleIcon />,
    tags: ['RERA', 'Legal Compliance', 'Tamil Nadu', 'Andhra Pradesh'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          The Real Estate (Regulation and Development) Act, 2016 (RERA) fundamentally changed how agents must operate in India. Non-compliance exposes you to penalties up to ₹10,000 per day of contravention. This checklist keeps you protected.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          TNRERA (Tamil Nadu) — Key Obligations
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Register as a real estate agent with TNRERA before facilitating any transaction involving a RERA-registered project.</Typography></li>
          <li><Typography variant="body1">Carry your TNRERA registration number and display it on all marketing materials including FindMyProps listings for registered projects.</Typography></li>
          <li><Typography variant="body1">Do not represent a project that has not received TNRERA registration. Verify registration status at <em>rera.tn.gov.in</em> before taking on any new mandate.</Typography></li>
          <li><Typography variant="body1">Maintain records of every transaction for 5 years including agreements, payments received, and correspondence with buyers and sellers.</Typography></li>
        </Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          APRERA (Andhra Pradesh) — Additional Considerations
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">APRERA requires annual renewal of agent registration. Set a calendar reminder 60 days before expiry.</Typography></li>
          <li><Typography variant="body1">Commission disclosure is mandatory — inform buyers in writing of your commission arrangement with the seller before signing any agreement.</Typography></li>
          <li><Typography variant="body1">Advertise only RERA-registered projects. Unapproved layout advertising is a common source of complaints and penalties.</Typography></li>
        </Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Best Practice: Pre-Listing Due Diligence
        </Typography>
        <Typography variant="body1" paragraph>
          Before listing any property on FindMyProps, confirm: EC (Encumbrance Certificate) is clear for minimum 13 years; patta is in seller's name; layout approval status (DTCP/CMDA/TNHB as applicable); and that no court stay or dispute is registered. Document your due diligence — it protects you and builds extraordinary buyer trust.
        </Typography>
      </>
    ),
  },
  {
    id: '10',
    category: 'Digital Marketing',
    readTime: '4 min read',
    title: 'WhatsApp Business for Real Estate: The Indian Agent\'s Complete Guide',
    summary:
      'Leverage WhatsApp Business catalogs, broadcast lists, and automated responses to manage hundreds of leads without missing a single inquiry.',
    icon: <PhoneAndroidIcon />,
    tags: ['WhatsApp Business', 'Lead Management', 'Automation'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          India has over 500 million WhatsApp users, and for real estate agents in Tier 1 and Tier 2 cities, WhatsApp is the primary channel for initial buyer communication. WhatsApp Business takes this further with professional tools designed for exactly your workflow.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Setting Up WhatsApp Business for Real Estate
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1"><strong>Business Profile:</strong> Add your RERA registration number, office address, and working hours. Link your FindMyProps profile in the website field.</Typography></li>
          <li><Typography variant="body1"><strong>Catalog:</strong> Create a property catalog with key listings. Each entry should have a photo, price, area, and a deep link to the full FindMyProps listing for complete details.</Typography></li>
          <li><Typography variant="body1"><strong>Quick Replies:</strong> Set up templates for common scenarios — price queries, site visit scheduling, document checklists, and follow-up after visits.</Typography></li>
          <li><Typography variant="body1"><strong>Away Message:</strong> An auto-response that acknowledges the inquiry and promises a response within 2 hours sets professional expectations and reduces lead drop-off.</Typography></li>
        </Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Broadcast Lists vs. Groups
        </Typography>
        <Typography variant="body1" paragraph>
          Always use Broadcast Lists over groups for property updates. Broadcasts maintain privacy (recipients don't see each other) and feel more personal. Segment lists by property type, budget range, and intent timeline for best results. Send no more than 2–3 broadcasts per week to avoid being muted.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Integrating with FindMyProps
        </Typography>
        <Typography variant="body1" paragraph>
          When a buyer contacts you via FindMyProps Direct Chat and the conversation progresses, offer to continue on WhatsApp for convenience. Share your WhatsApp Business link directly in the chat. This moves the relationship to your own channel while keeping the initial verified-lead qualification from FindMyProps.
        </Typography>
      </>
    ),
  },
  {
    id: '11',
    category: 'Agent Success',
    readTime: '5 min read',
    title: 'How FindMyProps\'s Partner Agent Program Helps You Grow Your Business',
    summary:
      'An overview of the FindMyProps partner agent ecosystem — exclusive benefits, verified status, and tools that give you a competitive edge.',
    icon: <GroupIcon />,
    tags: ['Partner Program', 'FindMyProps', 'Agent Benefits'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          FindMyProps is built as much for agents as it is for buyers. The platform's core thesis is that transparent, technology-enabled agents close more deals, and buyers make better decisions. The Partner Agent Program formalises this relationship with exclusive benefits.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          What You Get as a FindMyProps Partner Agent
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1"><strong>Verified Badge:</strong> A visible verification indicator on your profile and all your listings that signals legitimacy to buyers.</Typography></li>
          <li><Typography variant="body1"><strong>Priority Placement:</strong> Partner agent listings receive enhanced visibility in search results for their registered micro-markets.</Typography></li>
          <li><Typography variant="body1"><strong>Direct Chat Access:</strong> Unlimited messaging with verified buyers — no per-lead charges, no credits to buy.</Typography></li>
          <li><Typography variant="body1"><strong>Listing Analytics:</strong> Detailed view counts, reaction data, and inquiry trends for every listing in your portfolio.</Typography></li>
          <li><Typography variant="body1"><strong>Knowledge Hub Access:</strong> Exclusive market reports, legal guides, and digital marketing resources like this article.</Typography></li>
        </Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          The Verified Buyers Advantage
        </Typography>
        <Typography variant="body1" paragraph>
          One of the most cited pain points for Indian agents is dealing with window-shoppers and developers posing as buyers to extract market intelligence. FindMyProps's verification layer means every inquiry that reaches you is from a genuine, identity-verified user. Your time is the most valuable asset in real estate — spend it on buyers who have raised their hand as serious.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Getting Started
        </Typography>
        <Typography variant="body1" paragraph>
          Complete your FindMyProps profile, upload your RERA registration number, and list your first 3 properties with complete information and quality photos. Once your account meets the quality threshold, you qualify for the Partner Agent Program. Reach out through the Contact Us page for more details on the onboarding process.
        </Typography>
      </>
    ),
  },
  {
    id: '12',
    category: 'Digital Marketing',
    readTime: '4 min read',
    title: 'How to Use Data and Analytics to Sell Properties Faster in India',
    summary:
      'Turn FindMyProps listing analytics and Google Search Console data into actionable insights that reduce your average days-on-market.',
    icon: <LightbulbIcon />,
    tags: ['Analytics', 'Data-Driven Selling', 'Performance'],
    content: (
      <>
        <Typography variant="body1" paragraph>
          Top-performing agents across India are shifting from gut-feel pricing to data-informed decisions. Here is how to harness the analytics available to you as a FindMyProps agent.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Metrics That Matter in Your FindMyProps Dashboard
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1"><strong>Listing views per day:</strong> Falling views after week 2 indicate you need fresh photos or a price revision.</Typography></li>
          <li><Typography variant="body1"><strong>Reaction-to-inquiry ratio:</strong> High saves but low inquiries almost always means the price is above market. Reduce by 3–5% and monitor for 7 days.</Typography></li>
          <li><Typography variant="body1"><strong>Time-to-first-reply:</strong> Faster replies correlate directly with appointment booking rates. Aim for under 15 minutes during business hours.</Typography></li>
          <li><Typography variant="body1"><strong>Chat-to-site-visit conversion:</strong> If this is below 20%, review your chat scripts. The problem is usually too much information upfront — save the details for the in-person meeting.</Typography></li>
        </Box>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          Google Search Console for Your Listings
        </Typography>
        <Typography variant="body1" paragraph>
          If you have a personal real estate website or blog linked from your FindMyProps profile, add it to Google Search Console (free). You will see exactly which search queries are driving visitors — invaluable data for writing more of the content that works.
        </Typography>
        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          A/B Testing Listing Descriptions
        </Typography>
        <Typography variant="body1" paragraph>
          Run two identical listings with different primary photos or descriptions for 2-week periods. The higher-performing variant tells you what resonates with buyers in your micro-market. This systematic experimentation is the difference between agents who guess and agents who know.
        </Typography>
      </>
    ),
  },
];

const ALL_CATEGORIES = ['All', ...Array.from(new Set(articles.map(a => a.category)))];

export const AgentsKnowledgeHubContent: React.FC = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [openArticle, setOpenArticle] = useState<Article | null>(null);

  const filtered = articles.filter(a => {
    const matchesCategory = activeCategory === 'All' || a.category === activeCategory;
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      a.tags.some(t => t.toLowerCase().includes(q));
    return matchesCategory && matchesSearch;
  });

  return (
    <PageLayout.SectionContainer className={styles.root}>
      {/* Hero */}
      <Box className={styles.hero}>
        <Typography variant="h3" className={styles.heroTitle}>
          Agents Knowledge Hub
        </Typography>
        <Typography variant="body1" className={styles.heroSubtitle}>
          Expert guides, digital marketing strategies, and platform tutorials to help you
          grow your real estate business on FindMyProps.
        </Typography>

        <TextField
          id="knowledge-hub-search"
          fullWidth
          placeholder="Search articles, guides, and tips..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={styles.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Category Filter */}
      <Box className={styles.categoryRow}>
        {ALL_CATEGORIES.map(cat => (
          <Chip
            key={cat}
            label={cat}
            clickable
            onClick={() => setActiveCategory(cat)}
            color={activeCategory === cat ? 'primary' : 'default'}
            variant={activeCategory === cat ? 'filled' : 'outlined'}
            className={styles.categoryChip}
          />
        ))}
      </Box>

      {/* Stats Bar */}
      <Box className={styles.statsBar}>
        <Box className={styles.statItem}>
          <Typography variant="h5" className={styles.statNumber}>{articles.length}</Typography>
          <Typography variant="caption" className={styles.statLabel}>Articles</Typography>
        </Box>
        <Box className={styles.statItem}>
          <Typography variant="h5" className={styles.statNumber}>{ALL_CATEGORIES.length - 1}</Typography>
          <Typography variant="caption" className={styles.statLabel}>Categories</Typography>
        </Box>
        <Box className={styles.statItem}>
          <Typography variant="h5" className={styles.statNumber}>Free</Typography>
          <Typography variant="caption" className={styles.statLabel}>For All Agents</Typography>
        </Box>
      </Box>

      {/* Article Grid */}
      {filtered.length > 0 ? (
        <Box className={styles.grid}>
          {filtered.map(article => (
            <Box
              key={article.id}
              className={styles.card}
              onClick={() => setOpenArticle(article)}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && setOpenArticle(article)}
            >
              <Box className={styles.cardIconWrap}>
                {article.icon}
              </Box>
              <Box className={styles.cardMeta}>
                <Typography variant="caption" className={styles.cardCategory}>
                  {article.category}
                </Typography>
                <Typography variant="caption" className={styles.cardReadTime}>
                  {article.readTime}
                </Typography>
              </Box>
              <Typography variant="subtitle1" className={styles.cardTitle}>
                {article.title}
              </Typography>
              <Typography variant="body2" className={styles.cardSummary}>
                {article.summary}
              </Typography>
              <Box className={styles.cardTags}>
                {article.tags.slice(0, 2).map(tag => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" className={styles.tag} />
                ))}
              </Box>
              <Typography variant="caption" className={styles.readMoreLink}>
                Read article →
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Box className={styles.emptyState}>
          <Typography color="text.secondary">
            No articles found for your search. Try a different keyword or category.
          </Typography>
        </Box>
      )}

      {/* CTA Banner */}
      <Box className={styles.ctaBanner}>
        <Typography variant="h5" className={styles.ctaTitle}>
          Ready to grow your business on FindMyProps?
        </Typography>
        <Typography variant="body2" className={styles.ctaBody}>
          Join thousands of verified agents across South India connecting with serious, verified buyers every day.
          List your properties, engage via Direct Chat, and close more deals.
        </Typography>
        <Box
          component="a"
          href="/agents/create-property"
          className={styles.ctaButton}
        >
          List Your First Property
        </Box>
      </Box>

      {/* Article Modal */}
      <Dialog
        open={!!openArticle}
        onClose={() => setOpenArticle(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        {openArticle && (
          <>
            <DialogTitle className={styles.dialogTitle}>
              <Box className={styles.dialogTitleInner}>
                <Box>
                  <Typography variant="caption" className={styles.cardCategory}>
                    {openArticle.category} · {openArticle.readTime}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 0.5 }}>
                    {openArticle.title}
                  </Typography>
                </Box>
                <IconButton onClick={() => setOpenArticle(null)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
              <Divider sx={{ mt: 2 }} />
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              {openArticle.content}
              <Divider sx={{ my: 3 }} />
              <Box className={styles.dialogTags}>
                {openArticle.tags.map(tag => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" className={styles.tag} />
                ))}
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </PageLayout.SectionContainer>
  );
};
